import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { 
  ArrowLeft, Users, Plus, Trash2, Mail, Phone, Shield, Heart, FileText, 
  Home, Send, Loader2, UserPlus, Check, X, Clock, ChevronRight, Eye,
  Settings, AlertCircle, User, Wifi, WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { haptics } from '@/utils/haptics';
import { useDemo } from '@/contexts/DemoContext';
import { ProtectedFeaturePage } from '@/components/ProtectedFeaturePage';
import useFamilyWebSocket from '@/hooks/useFamilyWebSocket';

/**
 * FamilyNetworkPage - Complete Family Emergency Network management
 * FAMILY FEATURE - Requires Family subscription
 * - Invite family members
 * - Accept/decline invitations
 * - View shared binders from family
 * - Manage permissions
 */

const PERMISSION_OPTIONS = [
  { id: 'medical', label: 'Medical Info', icon: Heart, description: 'Allergies, medications, conditions, blood type' },
  { id: 'contacts', label: 'Emergency Contacts', icon: Phone, description: 'Contact list and relationships' },
  { id: 'doctor_summary', label: 'Doctor Summary', icon: FileText, description: 'Medical summary for appointments' },
  { id: 'documents', label: 'Documents', icon: FileText, description: 'Important documents list' },
  { id: 'household', label: 'Household Info', icon: Home, description: 'Home codes, WiFi, utilities' },
  { id: 'full_binder', label: 'Full Access', icon: Shield, description: 'Complete access including financial' },
];

const FamilyNetworkContent = () => {
  const navigate = useNavigate();
  const { isDemoMode, getFamilySharing, updateFamilySharing } = useDemo();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('network');
  
  // Network data
  const [familyMembers, setFamilyMembers] = useState([]);
  const [pendingSent, setPendingSent] = useState([]);
  const [pendingReceived, setPendingReceived] = useState([]);
  const [sharedBinders, setSharedBinders] = useState([]);
  
  // Invite dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [newInvite, setNewInvite] = useState({
    name: '',
    email: '',
    relationship: '',
    permissions: ['medical', 'contacts']
  });
  
  // Permissions dialog
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState([]);

  // Fetch network data
  const fetchNetwork = useCallback(async () => {
    try {
      setLoading(true);
      
      // Demo mode: use local demo data
      if (isDemoMode) {
        const familySharing = getFamilySharing() || {};
        setFamilyMembers(familySharing.family_members || []);
        setPendingSent(familySharing.pending_sent || []);
        setPendingReceived(familySharing.pending_received || []);
        setSharedBinders(familySharing.shared_binders || []);
        setLoading(false);
        return;
      }
      
      const response = await API.get('/emergency-binder/family/network');
      setFamilyMembers(response.data.family_members || []);
      setPendingSent(response.data.pending_sent || []);
      setPendingReceived(response.data.pending_received || []);
      setSharedBinders(response.data.shared_binders || []);
    } catch (error) {
      console.error('Failed to fetch family network:', error);
      if (!isDemoMode) {
        toast.error('Failed to load family network');
      }
    } finally {
      setLoading(false);
    }
  }, [isDemoMode, getFamilySharing]);

  // Real-time WebSocket updates (only in authenticated mode)
  const { isConnected: wsConnected } = useFamilyWebSocket({
    enabled: !isDemoMode,
    showToasts: true,
    onInvitationReceived: () => {
      // Refresh data when we receive a new invitation
      fetchNetwork();
    },
    onInvitationAccepted: () => {
      // Refresh data when our invitation is accepted
      fetchNetwork();
    },
    onInvitationDeclined: () => {
      // Refresh data when our invitation is declined
      fetchNetwork();
    },
    onMemberRemoved: () => {
      // Refresh data when a member is removed
      fetchNetwork();
    },
    onProfileUpdated: () => {
      // Refresh data when a family member updates their profile
      fetchNetwork();
    },
    onPermissionsChanged: () => {
      // Refresh data when permissions change
      fetchNetwork();
    },
  });

  useEffect(() => {
    fetchNetwork();
  }, [fetchNetwork]);

  const handleInvite = async () => {
    if (!newInvite.name || !newInvite.email) {
      toast.error('Please provide name and email');
      return;
    }

    try {
      setInviting(true);
      haptics.tap();
      
      // Demo mode: add to local state
      if (isDemoMode) {
        const newInviteItem = {
          id: `invite_${Date.now()}`,
          ...newInvite,
          status: 'pending'
        };
        const updatedPendingSent = [...pendingSent, newInviteItem];
        setPendingSent(updatedPendingSent);
        updateFamilySharing({ pending_sent: updatedPendingSent });
        toast.success(`Invitation sent to ${newInvite.name} (demo)`);
        setShowInviteDialog(false);
        setNewInvite({ name: '', email: '', relationship: '', permissions: ['medical', 'contacts'] });
        setInviting(false);
        return;
      }
      
      await API.post('/emergency-binder/family/invite', newInvite);
      toast.success(`Invitation sent to ${newInvite.name}`);
      setShowInviteDialog(false);
      setNewInvite({ name: '', email: '', relationship: '', permissions: ['medical', 'contacts'] });
      fetchNetwork();
    } catch (error) {
      console.error('Failed to send invite:', error);
      if (!isDemoMode) {
        toast.error(error.response?.data?.detail || 'Failed to send invitation');
      }
    } finally {
      setInviting(false);
    }
  };

  const handleRespondToInvitation = async (invitationId, action) => {
    try {
      haptics.tap();
      
      // Demo mode: update local state
      if (isDemoMode) {
        const updatedPendingReceived = pendingReceived.filter(inv => inv.id !== invitationId);
        setPendingReceived(updatedPendingReceived);
        if (action === 'accept') {
          const acceptedInv = pendingReceived.find(inv => inv.id === invitationId);
          if (acceptedInv) {
            const newMember = {
              id: `member_${Date.now()}`,
              name: acceptedInv.inviter_name,
              permissions: acceptedInv.permissions || ['medical', 'contacts']
            };
            const updatedMembers = [...familyMembers, newMember];
            setFamilyMembers(updatedMembers);
            updateFamilySharing({ family_members: updatedMembers, pending_received: updatedPendingReceived });
          }
        } else {
          updateFamilySharing({ pending_received: updatedPendingReceived });
        }
        toast.success(action === 'accept' ? 'Invitation accepted! (demo)' : 'Invitation declined (demo)');
        return;
      }
      
      await API.post(`/emergency-binder/family/respond/${invitationId}`, { action });
      toast.success(action === 'accept' ? 'Invitation accepted!' : 'Invitation declined');
      fetchNetwork();
    } catch (error) {
      console.error('Failed to respond to invitation:', error);
      if (!isDemoMode) {
        toast.error('Failed to respond to invitation');
      }
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      haptics.tap();
      
      // Demo mode: remove from local state
      if (isDemoMode) {
        const updatedMembers = familyMembers.filter(m => m.id !== memberId);
        const updatedPendingSent = pendingSent.filter(p => p.id !== memberId);
        setFamilyMembers(updatedMembers);
        setPendingSent(updatedPendingSent);
        updateFamilySharing({ family_members: updatedMembers, pending_sent: updatedPendingSent });
        toast.success('Family member removed (demo)');
        return;
      }
      
      await API.delete(`/emergency-binder/family/member/${memberId}`);
      toast.success('Family member removed');
      fetchNetwork();
    } catch (error) {
      console.error('Failed to remove member:', error);
      if (!isDemoMode) {
        toast.error('Failed to remove family member');
      }
    }
  };

  const handleUpdatePermissions = async () => {
    if (!selectedMember) return;
    
    try {
      haptics.tap();
      
      // Demo mode: update local state
      if (isDemoMode) {
        const updatedMembers = familyMembers.map(m => 
          m.id === selectedMember.id 
            ? { ...m, permissions: editingPermissions }
            : m
        );
        setFamilyMembers(updatedMembers);
        updateFamilySharing({ family_members: updatedMembers });
        toast.success('Permissions updated (demo)');
        setShowPermissionsDialog(false);
        return;
      }
      
      await API.put(`/emergency-binder/family/permissions/${selectedMember.id}`, {
        permissions: editingPermissions
      });
      toast.success('Permissions updated');
      setShowPermissionsDialog(false);
      fetchNetwork();
    } catch (error) {
      console.error('Failed to update permissions:', error);
      if (!isDemoMode) {
        toast.error('Failed to update permissions');
      }
    }
  };

  const openPermissionsDialog = (member) => {
    setSelectedMember(member);
    setEditingPermissions(member.permissions || []);
    setShowPermissionsDialog(true);
  };

  const togglePermission = (permissionId) => {
    setEditingPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  const toggleNewInvitePermission = (permissionId) => {
    setNewInvite(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const getPermissionLabel = (id) => {
    const perm = PERMISSION_OPTIONS.find(p => p.id === id);
    return perm?.label || id;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-32" data-testid="family-network-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/emergency-binder')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-slate-800">Family Emergency Network</h1>
                {/* Real-time connection indicator - only show in authenticated mode */}
                {!isDemoMode && (
                  <div 
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      wsConnected 
                        ? 'bg-emerald-50 text-emerald-600' 
                        : 'bg-slate-100 text-slate-400'
                    }`}
                    title={wsConnected ? 'Real-time updates active' : 'Connecting...'}
                  >
                    {wsConnected ? (
                      <Wifi className="h-2.5 w-2.5" />
                    ) : (
                      <WifiOff className="h-2.5 w-2.5" />
                    )}
                    <span className="hidden sm:inline">{wsConnected ? 'Live' : 'Offline'}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500">Share & access family emergency info</p>
            </div>
            <Button 
              onClick={() => setShowInviteDialog(true)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="invite-family-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Invite
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Pending Invitations Banner */}
        {pendingReceived.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <span className="font-medium text-amber-800">
                  {pendingReceived.length} Pending Invitation{pendingReceived.length > 1 ? 's' : ''}
                </span>
              </div>
              {pendingReceived.map((inv) => (
                <div key={inv.id} className="bg-white rounded-lg p-3 mb-2 last:mb-0">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-slate-800">{inv.inviter_name}</p>
                      <p className="text-sm text-slate-500">wants to share their emergency info with you</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => handleRespondToInvitation(inv.id, 'decline')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleRespondToInvitation(inv.id, 'accept')}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Accept
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="network">My Network</TabsTrigger>
            <TabsTrigger value="shared">Shared With Me</TabsTrigger>
          </TabsList>

          {/* My Network Tab */}
          <TabsContent value="network" className="space-y-4 mt-4">
            {/* Family Members */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-500" />
                  Family Members ({familyMembers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {familyMembers.length === 0 && pendingSent.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No family members in your network yet</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Invite family members to share emergency information
                    </p>
                    <Button 
                      onClick={() => setShowInviteDialog(true)} 
                      className="mt-4" 
                      variant="outline"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Invite Family
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Active members */}
                    {familyMembers.map((member) => (
                      <div key={member.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-800">{member.name}</h4>
                              {member.relationship && (
                                <p className="text-sm text-slate-500">{member.relationship}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openPermissionsDialog(member)}
                              className="text-slate-500"
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500"
                              onClick={() => handleRemoveMember(member.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {member.permissions?.map(perm => (
                            <Badge key={perm} variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                              {getPermissionLabel(perm)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Pending sent invitations */}
                    {pendingSent.map((inv) => (
                      <div key={inv.id} className="p-4 bg-slate-50 rounded-lg opacity-70">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                              <Clock className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-800">{inv.name}</h4>
                              <p className="text-sm text-amber-600 flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                Pending invitation
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-400"
                            onClick={() => handleRemoveMember(inv.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shared With Me Tab */}
          <TabsContent value="shared" className="space-y-4 mt-4">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-5 w-5 text-emerald-500" />
                  Family Binders I Can View ({sharedBinders.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {sharedBinders.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No shared binders yet</p>
                    <p className="text-sm text-slate-400 mt-1">
                      When family members share their emergency info with you, it will appear here
                    </p>
                  </div>
                ) : (
                  sharedBinders.map((binder, idx) => (
                    <button
                      key={`${binder.profile_id}-${idx}`}
                      onClick={() => navigate(`/emergency-binder/family/${binder.profile_id}`)}
                      className="w-full p-4 bg-slate-50 rounded-lg text-left hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Heart className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-800">{binder.profile_name}</h4>
                            <p className="text-sm text-slate-500">
                              Shared by {binder.owner_name}
                              {binder.relationship && ` • ${binder.relationship}`}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2 ml-13">
                        {binder.permissions?.slice(0, 3).map(perm => (
                          <Badge key={perm} variant="outline" className="text-xs">
                            {getPermissionLabel(perm)}
                          </Badge>
                        ))}
                        {binder.permissions?.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{binder.permissions.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Info Card */}
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-purple-800 mb-1">How Family Network Works</h3>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• Invite family members by email to join your network</li>
                  <li>• Control exactly what information each person can see</li>
                  <li>• View emergency info from family members who share with you</li>
                  <li>• All data stays private until you explicitly share it</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invite Family Member</DialogTitle>
            <DialogDescription>
              Send an invitation to share your emergency information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newInvite.name}
                  onChange={(e) => setNewInvite({...newInvite, name: e.target.value})}
                  placeholder="Full name"
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Relationship</Label>
                <Input
                  value={newInvite.relationship}
                  onChange={(e) => setNewInvite({...newInvite, relationship: e.target.value})}
                  placeholder="e.g., Spouse, Child"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newInvite.email}
                onChange={(e) => setNewInvite({...newInvite, email: e.target.value})}
                placeholder="email@example.com"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-3">
              <Label>What can they see?</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {PERMISSION_OPTIONS.map((perm) => {
                  const Icon = perm.icon;
                  return (
                    <div 
                      key={perm.id}
                      className="flex items-center space-x-3 p-2 rounded hover:bg-slate-50"
                    >
                      <Checkbox
                        id={`new-${perm.id}`}
                        checked={newInvite.permissions.includes(perm.id)}
                        onCheckedChange={() => toggleNewInvitePermission(perm.id)}
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={`new-${perm.id}`} 
                          className="text-sm font-medium flex items-center gap-2 cursor-pointer"
                        >
                          <Icon className="h-4 w-4 text-slate-500" />
                          {perm.label}
                        </label>
                        <p className="text-xs text-slate-500">{perm.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleInvite}
              disabled={inviting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="send-invite-btn"
            >
              {inviting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />Send Invite</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Permissions for {selectedMember?.name}</DialogTitle>
            <DialogDescription>
              Control what information this person can see
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4 max-h-64 overflow-y-auto">
            {PERMISSION_OPTIONS.map((perm) => {
              const Icon = perm.icon;
              return (
                <div 
                  key={perm.id}
                  className="flex items-center space-x-3 p-2 rounded hover:bg-slate-50"
                >
                  <Checkbox
                    id={`edit-${perm.id}`}
                    checked={editingPermissions.includes(perm.id)}
                    onCheckedChange={() => togglePermission(perm.id)}
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor={`edit-${perm.id}`} 
                      className="text-sm font-medium flex items-center gap-2 cursor-pointer"
                    >
                      <Icon className="h-4 w-4 text-slate-500" />
                      {perm.label}
                    </label>
                    <p className="text-xs text-slate-500">{perm.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdatePermissions}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Wrap content with feature protection - Family tier required
const FamilyNetworkPage = () => (
  <ProtectedFeaturePage
    feature="family_network"
    title="Network"
    description="Connect with family, friends, and caregivers to share emergency information securely."
    backPath="/emergency-binder"
  >
    <FamilyNetworkContent />
  </ProtectedFeaturePage>
);

export default FamilyNetworkPage;
